import { LegalPage } from "../../components/LegalPage";

export function RegulatoryHorizonMethodology() {
  return (
    <LegalPage
      title="Regulatory Horizon methodology"
      eyebrow="Trust center"
      description="How WeConsult Rx curates the Regulatory Horizon Watch: source criteria, citation discipline, quarterly review cadence, and our pre-position commitment."
      path="/trust/regulatory-horizon-methodology"
      effective="2026-05-31"
    >
      <p>
        The Regulatory Horizon module in the WeConsult Rx platform tracks
        upcoming legal and regulatory changes that impact long-term care
        pharmacy practice and pharmacist consulting. This page explains how
        the curated catalog is built, sourced, reviewed, and surfaced to
        customers.
      </p>

      <h2>Sources</h2>
      <p>
        Every entry in the Regulatory Horizon catalog is anchored to a
        verbatim citation and a primary source URL. Primary sources are:
      </p>
      <ul>
        <li>
          <strong>Federal Register</strong> (federalregister.gov) — NPRMs,
          final rules, and notices issued by HHS OCR, CMS, FDA, SAMHSA, DEA.
        </li>
        <li>
          <strong>State legislature and code websites</strong> — for state
          consumer-health-privacy laws (e.g., Washington RCW, Nevada NRS),
          board of pharmacy rules, and scope-of-practice legislation.
        </li>
        <li>
          <strong>EU Official Journal (eur-lex.europa.eu)</strong> — for the
          EU AI Act and eIDAS updates, when in scope.
        </li>
        <li>
          <strong>NABP and NASPA program pages</strong> — for partnership
          programs (CPE Monitor) and the state pharmacist scope tracker.
        </li>
        <li>
          <strong>CMS State Operations Manual</strong> — for F-tag context
          and Mega-Rule signals where no NPRM has been filed yet.
        </li>
      </ul>
      <p>
        We do not cite blog posts, op-eds, or trade-press analyses as
        primary sources. We may link them as context in the readiness notes
        when they help explain industry interpretation, but the citation
        field is always the rule text itself.
      </p>

      <h2>Citation discipline</h2>
      <p>
        Every entry carries:
      </p>
      <ul>
        <li>
          <strong>citation</strong> — the formal cite (e.g.,{" "}
          <em>89 FR 32976 (April 26, 2024)</em>) verbatim and unique.
        </li>
        <li>
          <strong>source_url</strong> — a direct link to the canonical
          document on federalregister.gov, the state legislature site, or
          eur-lex.europa.eu.
        </li>
        <li>
          <strong>nprm_published_at / final_rule_published_at / compliance_effective_date</strong>{" "}
          — ISO dates so the dashboard can compute countdowns.
        </li>
      </ul>

      <h2>Pre-position commitment</h2>
      <p>
        For every entry we publish a transparent assessment of WeConsult Rx
        readiness:
      </p>
      <ul>
        <li>
          <strong>Have</strong> — the rule&apos;s technical or procedural
          requirements are already shipped in our platform.
        </li>
        <li>
          <strong>Partial</strong> — we have shipped some controls; gaps
          are listed in the readiness notes.
        </li>
        <li>
          <strong>Not yet</strong> — we have not shipped controls for this
          rule. Planned work appears in the readiness notes.
        </li>
        <li>
          <strong>N/A</strong> — the rule does not apply to our platform
          (e.g., DSCSA only applies to dispensing).
        </li>
        <li>
          <strong>Monitoring</strong> — no NPRM has been filed yet. We
          surface the entry so customers are not surprised when a rule
          appears.
        </li>
      </ul>
      <p>
        The readiness assessment is renewed every quarter as part of the
        scheduled refresh job, and updated mid-quarter when a rule lands or
        we ship a relevant change.
      </p>

      <h2>Quarterly review cadence</h2>
      <p>
        The Regulatory Horizon catalog is reviewed every quarter on the
        first business day of January, April, July, and October. The
        canonical curated content lives in a JSON file maintained in our
        application repository; updates flow through the same code-review
        process as every other change. An automated worker re-seeds the
        catalog from the file. Customers see updated entries within 24
        hours of merge.
      </p>

      <h2>Tenant personalization</h2>
      <p>
        Customers declare their operating jurisdictions (US states plus EU)
        and the facility types they consult on (skilled nursing, assisted
        living, ambulatory surgery, dialysis, pharmacy). The dashboard
        filters entries to the intersection of those preferences and each
        entry&apos;s declared scope. Entries marked as universally
        applicable (such as the HHS OCR HIPAA Security Rule NPRM) appear
        for every tenant regardless of declared jurisdiction.
      </p>

      <h2>Acknowledgements</h2>
      <p>
        Tenant administrators may acknowledge each entry and capture a
        compliance plan note. Acknowledgements are tenant-scoped, with
        row-level security enforcing isolation. The free-text notes never
        appear in our audit metadata — only the entry id, regime, and a
        flag indicating whether notes were captured.
      </p>

      <h2>Disclaimer</h2>
      <p>
        The Regulatory Horizon Watch is provided as an{" "}
        <strong>educational reference</strong> for our customers. It is
        <strong> not legal advice</strong>. Operators should always confirm
        applicability and compliance posture with their own compliance
        counsel.
      </p>
    </LegalPage>
  );
}
